// Character provenance → display rows for the launcher's Properties panel
// (ENG-1032).
//
// Pure and injectable: `now` is a parameter, the same contract as
// relativeTime.js next door, so every case here is testable without freezing a
// clock or mounting React.
//
// The rule this module exists to enforce: an absent field renders as an
// explicit "unknown" WITH A REASON, never as a blank and never as a plausible
// substitute. The panel's whole job is helping someone decide whether a
// character can be deleted, so a fabricated owner is worse than no owner — and
// a blank row just makes the reader wonder whether the UI is broken.
//
// Two timestamps that look interchangeable and are not:
//
//   updatedAt       the character CONFIG row's own mtime. Only five server
//                   paths move it (the character upsert, rig upload, pack path,
//                   the materials jsonb write, material mapping). Adding an
//                   animation, texture, material or blueprint does NOT.
//   lastActivityAt  server-computed MAX across every character-scoped table.
//
// Measured on staging while building this: myra's assets had been edited across
// 2026-08-13..08-20 with nothing touching its config row. So labelling
// `updatedAt` as "last modified" would tell someone a heavily-used character
// was untouched — the exact opposite of the answer they came for. They are
// shown as two separate rows, labelled for what they actually are.

import { formatRelativeTime } from './relativeTime.js'

/** Go marshals a zero time.Time as year 1 rather than null, so an unset
    timestamp arrives looking like a real date. Anything at or before this is
    treated as absent. */
const GO_ZERO_TIME_MS = Date.parse('0002-01-01T00:00:00Z')

/**
 * Parse a server timestamp into epoch ms.
 *
 * @param {unknown} value ISO-8601 string, epoch ms number, or null/absent.
 * @returns {number|null} null when the value is missing, unparseable, or Go's
 *   zero time — callers render that as unknown rather than as "over a year ago",
 *   which is what a year-1 date would otherwise produce.
 */
export function parseTimestamp(value) {
  if (value == null) return null
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > GO_ZERO_TIME_MS ? value : null
  }
  if (typeof value !== 'string' || value === '') return null
  const ms = Date.parse(value)
  if (!Number.isFinite(ms)) return null
  return ms > GO_ZERO_TIME_MS ? ms : null
}

/**
 * Human label for a stored actor.
 *
 * Email first because it is the only part a person can read. The server cannot
 * turn a stored id into a name after the fact — avatar-auth-service gates its
 * /users lookup behind super-admin/org-admin — which is why the email is
 * captured at write time and why an id-only record falls back to a shortened
 * id rather than to a name we would have to invent.
 *
 * @returns {string|null} null when nothing is recorded.
 */
export function actorLabel({ id, email } = {}) {
  const e = typeof email === 'string' ? email.trim() : ''
  if (e) return e
  const i = typeof id === 'string' ? id.trim() : ''
  if (!i) return null
  // A bare uuid is not a person, but it is evidence SOMETHING was recorded, and
  // it is enough to match against an audit log. Shortened because the full 36
  // characters push the value column wide for no added meaning.
  return i.length > 12 ? `${i.slice(0, 8)}…` : i
}

/** Reasons an absent value is absent. Distinct strings because they lead to
    different actions: one is expected for old records, the other means nobody
    has touched the character at all. */
const NOT_RECORDED = 'not recorded — this character predates provenance tracking'
const NO_ACTIVITY = 'no recorded activity on this character or its assets'
const NO_SERVER_FIELD = 'not reported by this server — the API predates ENG-1032'

/**
 * A single display row.
 * @typedef {object} PropertyRow
 * @property {string} key      stable id, also the test hook
 * @property {string} label    left column
 * @property {string} value    right column — the reason string when unknown
 * @property {string|null} title  hover text: the exact timestamp, so the reader
 *   never has to do arithmetic on "3 weeks ago" but is not shown it by default
 * @property {boolean} unknown render disabled-with-reason, never hidden
 */

function timeRow(key, label, raw, now, absentReason) {
  const ms = parseTimestamp(raw)
  if (ms == null) {
    return { key, label, value: absentReason, title: null, unknown: true }
  }
  // formatRelativeTime returns null only for unusable input, which parseTimestamp
  // has already excluded — the fallback is belt-and-braces, not a live path.
  const rel = formatRelativeTime(ms, now)
  return {
    key,
    label,
    value: rel ?? new Date(ms).toISOString(),
    title: new Date(ms).toISOString(),
    unknown: false,
  }
}

function actorRow(key, label, id, email, absentReason) {
  const who = actorLabel({ id, email })
  if (!who) return { key, label, value: absentReason, title: null, unknown: true }
  // The full id in the tooltip: the label may be an email or a truncated id,
  // and an audit trail needs the whole thing.
  const full = typeof id === 'string' && id.trim() ? id.trim() : null
  return { key, label, value: who, title: full, unknown: false }
}

/**
 * The ORIGIN row (ENG-1044 Task 16): duplicate provenance.
 *
 * `duplicatedFrom` is a SNAPSHOT taken at duplicate time — `{slug, label}` of
 * the character this one was copied from, as they were at that moment. The
 * source may since have been renamed or even deleted, so this is never
 * rendered as a link and never re-resolved against the live source; the
 * tooltip says so rather than the value silently going stale next to a
 * character that no longer matches it.
 *
 * Absent is not "unknown" the way a missing timestamp is — it is the
 * definite fact that this character is an original (or predates the
 * feature, which reads identically). Both states are always shown, per this
 * panel's rule that absence is explained, never hidden.
 *
 * `testid` overrides the row's default `char-prop-{key}` hook so the two
 * states are independently queryable in tests.
 *
 * @param {object} character a row from GET /api/characters
 * @returns {PropertyRow & {testid: string}}
 */
export function originRow(character) {
  const from = character?.duplicatedFrom
  const slug = typeof from?.slug === 'string' ? from.slug.trim() : ''
  const label = typeof from?.label === 'string' ? from.label.trim() : ''

  if (from && (slug || label)) {
    const name = label || slug
    return {
      key: 'origin',
      label: 'Origin',
      value: slug ? `Duplicated from ${name} (${slug})` : `Duplicated from ${name}`,
      title: 'A snapshot taken at the time of duplication — the source may since ' +
        'have been renamed or deleted.',
      unknown: false,
      testid: 'char-origin-duplicate',
    }
  }

  return {
    key: 'origin',
    label: 'Origin',
    value: 'Original',
    title: null,
    unknown: false,
    testid: 'char-origin-original',
  }
}

/**
 * Ordered display rows for one character.
 *
 * @param {object} character  a row from GET /api/characters
 * @param {number} now        epoch ms, injected
 * @returns {PropertyRow[]}
 */
export function characterPropertyRows(character, now) {
  if (!character) return []

  // A server that reports no provenance AT ALL is a different situation from a
  // character with none: it means this client is talking to an asset-manager
  // from before ENG-1032, and the fix is a deploy, not an edit. Naming that
  // separately stops someone hunting for a character-level cause.
  const serverReports = 'createdAt' in character || 'lastActivityAt' in character
  const missing = serverReports ? NOT_RECORDED : NO_SERVER_FIELD

  return [
    timeRow('createdAt', 'Created', character.createdAt, now, missing),
    actorRow('createdBy', 'Created by', character.createdBy, character.createdByEmail, missing),
    timeRow('lastActivityAt', 'Last activity', character.lastActivityAt, now,
      serverReports ? NO_ACTIVITY : NO_SERVER_FIELD),
    timeRow('updatedAt', 'Config modified', character.updatedAt, now, missing),
    actorRow('updatedBy', 'Modified by', character.updatedBy, character.updatedByEmail, missing),
  ]
}

/**
 * The one-line "modified <when>" label for a launcher card.
 *
 * Reads lastActivityAt, NOT updatedAt — on a card there is room for exactly one
 * number, and the one worth showing is "has anyone touched this", which is the
 * question the grid is being scanned to answer.
 *
 * @returns {string|null} null renders nothing at all. A card is not the place
 *   to explain an absence; the panel is, and it does.
 */
export function cardActivityLabel(character, now) {
  const ms = parseTimestamp(character?.lastActivityAt)
  if (ms == null) return null
  const rel = formatRelativeTime(ms, now)
  return rel ? `modified ${rel}` : null
}
